import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import useLinksGestor from './hooks/useLinksGestor';
import GestorSidebar from './components/GestorSidebar';
import GestorAreaSelector from './components/GestorAreaSelector';
import GestorSearchBar from './components/GestorSearchBar';
import GestorSectionCard from './components/GestorSectionCard';
import GestorEmptySearch from './components/GestorEmptySearch';
import BurbujasLoader from '../../components/UIElements/BurbujasLoader';

const LinksNuevoGestor = () => {
  const {
    query,
    setQuery,
    section,
    selectSection,
    area,
    setArea,
    areaOptions,
    isAdmin,
    isSub,
    navItems,
    shownSections,
    empty,
    loading,
    error,
    tiposDeAccesos,
    editMode,
    setEditMode,
    addSection,
    updateSection,
    addLink,
    updateLink,
    deleteLink,
    deleteSection,
    reorderSections,
    reorderLinks,
  } = useLinksGestor();

  const [showNewSectionForm, setShowNewSectionForm] = useState(false);
  const [newSectionTitle, setNewSectionTitle] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogError, setDialogError] = useState('');
  const [pendingAction, setPendingAction] = useState(null);

  const requestDelete = (target) => {
    setPendingAction(target);
    setDialogError('');
    setDialogOpen(true);
  };

  const performConfirmedAction = async () => {
    if (!pendingAction) return;
    try {
      setDialogError('');
      if (pendingAction.kind === 'link') {
        await deleteLink(pendingAction.sectionId, pendingAction.index, pendingAction.link);
      } else if (pendingAction.kind === 'section') {
        await deleteSection(pendingAction.id);
      }
      setDialogOpen(false);
      setPendingAction(null);
    } catch (err) {
      setDialogError(err.message || 'No se pudo completar la operación.');
    }
  };

  const handleAddSection = async (event) => {
    event.preventDefault();
    if (!newSectionTitle.trim()) return;
    try {
      await addSection({ title: newSectionTitle });
      setNewSectionTitle('');
      setShowNewSectionForm(false);
    } catch (err) {
      console.error('Error al crear tarjeta:', err);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '60vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#F7FBFD',
        }}
      >
        <BurbujasLoader />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100%',
        fontFamily: (theme) => theme.typography.h1.fontFamily,
        color: '#1E2433',
        backgroundColor: '#F7FBFD',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          component="main"
          sx={{
            width: '100%',
            minWidth: 0,
            p: { xs: '24px 20px 40px', sm: '36px 40px 56px' },
            boxSizing: 'border-box',
          }}
        >
          <Box
            sx={{
              width: '100%',
              maxWidth: 1240,
              mx: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: { xs: '22px', md: '30px' },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'stretch', sm: 'flex-start' },
                gap: 2,
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <Typography
                  component="h1"
                  sx={{
                    m: 0,
                    fontSize: { xs: 28, sm: 36 },
                    fontWeight: 700,
                    letterSpacing: '-0.02em',
                    color: '#1E2433',
                    lineHeight: 1.15,
                  }}
                >
                  Accesos del equipo
                </Typography>
                <Typography sx={{ m: 0, fontSize: 17, color: '#4F5868' }}>
                  Todos los enlaces que usamos a diario, en un solo lugar.
                </Typography>
              </Box>
              {isAdmin && (
                <Button
                  variant={editMode ? 'contained' : 'outlined'}
                  color={editMode ? 'success' : 'primary'}
                  onClick={() => {
                    setEditMode((value) => !value);
                    setShowNewSectionForm(false);
                  }}
                  sx={{
                    alignSelf: { xs: 'stretch', sm: 'flex-start' },
                    whiteSpace: 'nowrap',
                    borderRadius: '8px',
                    px: 1.5,
                    py: 0.75,
                    fontWeight: 600,
                    fontSize: 13,
                    textTransform: 'none',
                    boxShadow: 'none',
                    minHeight: 34,
                  }}
                >
                  {editMode ? 'Finalizar edición' : 'Activar edición'}
                </Button>
              )}
            </Box>

            {isSub && (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
                <GestorAreaSelector areaOptions={areaOptions} currentArea={area} onSelectArea={setArea} />
              </Box>
            )}

            {error && <Alert severity="error">{error}</Alert>}

            {isAdmin ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '22px' }}>
                {editMode && (
                  <Alert severity="info" sx={{ borderRadius: '12px' }}>
                    Modo edición activado. Podés agregar, editar o eliminar tarjetas y enlaces, o arrastrarlos para reordenar la vista.
                  </Alert>
                )}
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'minmax(320px, 1.7fr) minmax(220px, 0.9fr) auto' },
                    gap: 1.5,
                    alignItems: 'end',
                  }}
                >
                  <GestorSearchBar query={query} onQueryChange={setQuery} />
                  <GestorSidebar navItems={navItems} currentSection={section} onSelectSection={selectSection} />
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setQuery('');
                      selectSection('todas');
                    }}
                    disabled={!query.trim() && section === 'todas'}
                    sx={{
                      minHeight: 48,
                      px: 2,
                      borderRadius: '12px',
                      textTransform: 'none',
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                      boxShadow: 'none',
                    }}
                  >
                    Limpiar filtros
                  </Button>
                </Box>

                {editMode && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1.5 }}>
                    <Button
                      variant="contained"
                      onClick={() => setShowNewSectionForm((value) => !value)}
                      sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700 }}
                    >
                      {showNewSectionForm ? 'Cancelar nueva tarjeta' : '+ Agregar tarjeta'}
                    </Button>
                    {showNewSectionForm && (
                      <Box
                        component="form"
                        onSubmit={handleAddSection}
                        sx={{
                          width: '100%',
                          maxWidth: 520,
                          p: 2,
                          display: 'flex',
                          flexDirection: { xs: 'column', sm: 'row' },
                          gap: 1.25,
                          border: '1px solid #D8EAF4',
                          borderRadius: '14px',
                          backgroundColor: '#FFFFFF',
                        }}
                      >
                        <TextField
                          size="small"
                          label="Nombre de la tarjeta"
                          placeholder="Ej. Herramientas internas"
                          value={newSectionTitle}
                          onChange={(e) => setNewSectionTitle(e.target.value)}
                          required
                          fullWidth
                        />
                        <Button
                          type="submit"
                          variant="contained"
                          sx={{ borderRadius: '9px', textTransform: 'none', whiteSpace: 'nowrap' }}
                        >
                          Crear tarjeta
                        </Button>
                      </Box>
                    )}
                  </Box>
                )}

                {empty && !editMode && Boolean(query.trim()) && <GestorEmptySearch query={query} />}
                {!empty && (
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 310px), 1fr))',
                      gap: '22px',
                      alignItems: 'start',
                    }}
                  >
                    {shownSections.map((sec) => (
                      <GestorSectionCard
                        key={sec.id}
                        section={sec}
                        editMode={editMode}
                        tiposDeAccesos={tiposDeAccesos}
                        queryActive={Boolean(query.trim())}
                        onAddLink={addLink}
                        onEditLink={updateLink}
                        onEditSection={updateSection}
                        onDeleteLink={(sectionId, index, link) =>
                          requestDelete({ kind: 'link', sectionId, index, link, title: link.name })
                        }
                        onDeleteSection={() => requestDelete({ kind: 'section', ...sec })}
                        onReorderLinks={reorderLinks}
                        onSectionDrop={reorderSections}
                      />
                    ))}
                  </Box>
                )}
                {empty && (editMode || !query.trim()) && (
                  <Typography sx={{ color: '#64748B' }}>
                    {query.trim()
                      ? 'No hay tarjetas que coincidan con la búsqueda. Borrá el texto para ver todas las tarjetas.'
                      : 'Todavía no hay tarjetas cargadas en esta área.'}
                  </Typography>
                )}
              </Box>
            ) : null}
          </Box>
        </Box>
      </Box>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 700, fontFamily: (theme) => theme.typography.h1.fontFamily }}>
          ¿Confirmás la eliminación?
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
          <Typography>
            {pendingAction?.kind === 'link' ? (
              <>
                Vas a eliminar el enlace <strong>{pendingAction?.title}</strong>. Esta acción no se puede deshacer. ¿Querés continuar?
              </>
            ) : (
              <>
                Vas a eliminar la tarjeta <strong>{pendingAction?.title}</strong> y todos sus enlaces asociados. Esta acción no se puede deshacer. ¿Querés continuar?
              </>
            )}
          </Typography>
          {dialogError && <Alert severity="error">{dialogError}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none' }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={performConfirmedAction}
            sx={{ textTransform: 'none', borderRadius: '10px' }}
          >
            Sí, eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LinksNuevoGestor;
